<template>
  <div class="form-fields spacious">
    <!-- Field selector -->
    <div class="field editing">
      <ItemDetailField :label="capitalize(editType)" :is-editing="true">
        <div class="is-flex-grow-1">
          <div
            class="is-flex is-align-items-center ml-1"
            style="gap: var(--space-2)"
          >
            <div class="buttons has-addons mb-0 type-toggle">
              <button
                class="button is-small mb-0"
                :class="{ 'is-primary is-selected': editType === 'attribute' }"
                @click="selectType('attribute')"
              >
                Attributes
              </button>
              <button
                class="button is-small mb-0"
                :class="{ 'is-primary is-selected': editType === 'annotation' }"
                :disabled="!allowAnnotations"
                @click="selectType('annotation')"
              >
                Annotations
              </button>
            </div>
            <a class="is-size-7 ml-auto" @click="selectType('attribute')">
              Reset form
            </a>
          </div>
          <SelectContainer
            class="mb-1"
            label="label"
            track-by="value"
            :class="{ 'is-danger': editedField === null }"
            :placeholder="`Select the ${editType} to be edited`"
            :options="editedFieldOptions"
            :value="editedField"
            :tabindex="0"
            @input="(v) => (editedField = v)"
          />
          <p v-if="editedField === null" class="is-size-7 has-text-danger pl-2">
            Pick an {{ editType }}
          </p>
          <ul v-else class="is-size-7">
            <li v-if="!isSingleValueField">
              <span class="icon is-small has-text-info mr-1">
                <i class="fa-solid fa-info-circle" />
              </span>
              <b>{{ editedField.label }}</b>
              accepts a set of values
            </li>
            <li
              v-if="
                editedField.value === 'stock_locations' ||
                editedField.value === 'channels'
              "
            >
              <span class="icon is-small has-text-info mr-1">
                <i class="fa-solid fa-info-circle" />
              </span>
              It is currently only possible to replace
              <b>{{ editedField.label }}</b>
            </li>
          </ul>
          <div
            v-if="isChangingOwnedBy"
            class="notification is-fullwidth is-info is-light is-align-items-center p-2 mx-0"
          >
            <span class="icon is-small has-text-info">
              <i class="fa-solid fa-info-circle" />
            </span>
            <span class="help text">
              Changing the item
              <b>{{ editedField.label }}</b>
              adds permissions for the newly added group. In the meantime, the
              permissions set for the current group will be kept. They would
              need to be further adjusted with the "batch sharing" feature if
              needed.
            </span>
          </div>
        </div>
      </ItemDetailField>
    </div>

    <!-- Operation -->
    <div class="field editing">
      <ItemDetailField label="Operation" :is-editing="true">
        <div
          class="is-flex is-flex-direction-column"
          style="gap: var(--space-1)"
        >
          <fieldset
            :disabled="!hasSelectedField"
            class="is-flex"
            style="gap: var(--space-2)"
          >
            <div class="buttons has-addons mb-0">
              <button
                v-for="op in batchEditOps"
                :key="`edit-operation-${op}`"
                class="button is-small mb-0"
                :class="{ 'is-primary': chosenOperation === op }"
                @click="chosenOperation = op"
              >
                {{ capitalize(op) }}
              </button>
            </div>
            <button
              v-if="canUnset"
              class="button is-small is-danger mb-0"
              :class="{ 'is-outlined': chosenOperation !== 'unset' }"
              @click="chosenOperation = 'unset'"
            >
              Unset
            </button>
          </fieldset>
          <ul v-if="hasSelectedField" class="is-size-7">
            <li v-if="chosenOperation === 'replace'">
              <span class="icon is-small has-text-info mr-1">
                <i class="fa-solid fa-info-circle" />
              </span>
              <code class="has-text-primary">Replace</code>
              replaces the existing
              <span v-if="!isSingleValueField">
                list of values for
                <b>{{ editedField.label }}</b>
              </span>
              <span v-else>
                <b>{{ editedField.label }}</b>
                value
              </span>
            </li>
            <li v-if="chosenOperation === 'add'">
              <span class="icon is-small has-text-info mr-1">
                <i class="fa-solid fa-info-circle" />
              </span>
              <code class="has-text-primary">Add</code>
              the value to the existing set of
              <b>{{ editedField.label }}</b>
            </li>
            <li v-if="chosenOperation === 'remove'">
              <span class="icon is-small has-text-info mr-1">
                <i class="fa-solid fa-info-circle" />
              </span>
              <code class="mr-1 has-text-primary">Remove</code>
              the value from the existing set of
              <b>{{ editedField.label }}</b>
            </li>
            <li v-if="chosenOperation === 'unset'">
              <span class="icon is-small has-text-info mr-1">
                <i class="fa-solid fa-info-circle" />
              </span>
              <code class="has-text-primary">Unset</code>
              the
              <b>{{ editedField.label }}</b>
              {{ editType }}
              from the items
            </li>
          </ul>
        </div>
      </ItemDetailField>
    </div>

    <!-- Value -->
    <div v-if="!isUnsetting" class="field editing">
      <ItemDetailField label="Value" :is-editing="true">
        <div
          class="is-flex is-align-items-baseline is-flex-grow-1"
          style="gap: var(--space-2)"
        >
          <div class="is-flex-grow-1">
            <FieldComponent
              v-if="editedField"
              :key="`batchedit-value-${editedField.name}`"
              :app-name="appName"
              page="detail"
              :field-meta="editedField.model"
              :data="editedValue"
              :is-edit-mode="true"
              @edit="(value) => (editedValue = { value })"
            />
            <input v-else class="input is-small" type="text" disabled />
          </div>
          <BaseButton
            :icon="['fa-solid', 'fa-undo']"
            size="small"
            :outlined="true"
            :variant="editedField === null ? undefined : 'primary'"
            label="Default"
            :disabled="editedField === null"
            @click="setDefault"
          />
        </div>
      </ItemDetailField>
    </div>
  </div>

  <!-- Summary notification (outside grid, full width) -->
  <div
    v-if="editedField !== null && chosenOperation && editedValue"
    class="notification is-fullwidth is-info is-light mt-3 py-4"
  >
    <div class="text">
      <p v-if="isUnsetting">
        <b>{{ editedField.label }}</b>
        will be
        <b class="has-text-danger">removed</b>
        from the items.
      </p>
      <template v-else>
        <p class="pr-1">
          Upon saving, the value of the
          <code class="has-text-primary">{{ editedField.label }}</code>
          {{ editType }} of all editable items will be updated.
        </p>
        <p>
          The specified value
          <span v-if="!isSingleValueField">set</span>
          will
          <b>
            <span v-if="chosenOperation === 'replace'" class="has-text-danger">
              replace
            </span>
            <span
              v-else-if="chosenOperation === 'add'"
              class="has-text-success"
            >
              be added to
            </span>
            <span
              v-else-if="chosenOperation === 'remove'"
              class="has-text-danger"
            >
              be removed from
            </span>
          </b>
          the existing value
          <span v-if="!isSingleValueField">set</span>
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { computed, ref, watch } from 'vue'

import { capitalize } from '@/filters/filters'

import ItemDetailField from '@/components/fields/ItemDetailField.vue'
import SelectContainer from '@/components/SelectContainer.vue'

const BATCH_EDIT_OPERATIONS = ['replace', 'add', 'remove']

export default {
  name: 'BatchEditForm',
  components: {
    SelectContainer,
    ItemDetailField,
  },
  props: {
    appName: { type: String, default: '' },
    editType: { type: String, default: 'attribute' },
    editedFieldOptions: { type: Array, default: () => [] },
    allowAnnotations: { type: Boolean, default: false },
  },
  emits: ['select-edit-type', 'payload-unit-change'],
  setup(props, { emit }) {
    const v$ = useVuelidate()
    const chosenOperation = ref(BATCH_EDIT_OPERATIONS[0])
    const editedField = ref(null)
    const editedValue = ref(null)

    const hasSelectedField = computed(() => !!editedField.value?.value)
    const isChangingOwnedBy = computed(
      () => editedField.value?.value === 'owned_by',
    )

    const isSingleValueField = computed(
      () =>
        editedField.value?.value === 'channels' ||
        editedField.value?.value === 'stock_locations' ||
        editedField.value?.multiple === false,
    )

    const batchEditOps = computed(() =>
      isSingleValueField.value ? ['replace'] : BATCH_EDIT_OPERATIONS,
    )

    const canUnset = computed(() => props.editType === 'annotation')
    const isUnsetting = computed(() => chosenOperation.value === 'unset')

    const payloadUnit = computed(() => {
      const id = editedField.value?.name || null
      const path = editedField.value?.value
        ? `/${editedField.value.value}`
        : null
      const op = chosenOperation.value || null
      const value =
        editedValue.value?.value === false || editedValue.value?.value
          ? editedValue.value?.value
          : null
      if (!path || !op) return null
      return {
        id,
        label: editedField.value?.label,
        path,
        value,
        op: chosenOperation.value,
        validated: v$.value.$errors.length === 0,
      }
    })

    function setDefault() {
      editedValue.value = null
      if (editedField.value) {
        editedValue.value = {
          name: editedField.value.name,
          value: editedField.value.default,
        }
      }
    }

    function selectType(type) {
      editedField.value = null
      emit('select-edit-type', type)
    }

    // Reset default when field changes
    watch(editedField, () => setDefault())

    // Ensure operation is valid for current field
    watch(
      batchEditOps,
      (ops) => {
        if (!ops.includes(chosenOperation.value)) {
          chosenOperation.value = ops[0] || null
        }
      },
      { immediate: true },
    )

    // Emit payload changes
    watch(payloadUnit, (u) => emit('payload-unit-change', u), {
      immediate: true,
    })

    return {
      chosenOperation,
      editedField,
      editedValue,
      hasSelectedField,
      isChangingOwnedBy,
      isSingleValueField,
      batchEditOps,
      canUnset,
      isUnsetting,
      setDefault,
      selectType,
      capitalize,
    }
  },
}
</script>

<style lang="scss" scoped>
// Batch-edit form sits inside a modal that already has body padding — drop the
// shared .form-fields horizontal padding so fields span the full modal width.
.form-fields {
  padding-left: 0;
  padding-right: 0;
}

// Square the bottom corners so the toggle sits flush against the select below it.
.type-toggle .button {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
</style>
